import React, { useEffect, useState } from "react";
import { api, formatApiError, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar, Copy, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Dialog used on every BlockCard to move/copy a programmed section to another date —
 * same athlete or a different one. Triggers a parent refresh on success.
 */
export default function MoveBlockDialog({
  programId,
  dayIdx,
  blockId,
  blockTitle,
  athleteId,
  trigger,
  onDone,
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("move"); // "move" | "copy"
  const [toDate, setToDate] = useState(() => todayISO());
  const [toAthleteId, setToAthleteId] = useState(athleteId);
  const [athletes, setAthletes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && athletes.length === 0) {
      api.get("/users", { params: { role: "athlete" } })
        .then((r) => setAthletes(r.data))
        .catch(() => {});
    }
  }, [open, athletes.length]);

  useEffect(() => {
    if (open) {
      setToAthleteId(athleteId);
    }
  }, [open, athleteId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!toDate) { toast.error("Pick a target date"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/programs/${programId}/days/${dayIdx}/blocks/${blockId}/move`,
        {
          to_date: toDate,
          mode,
          to_athlete_id: toAthleteId !== athleteId ? toAthleteId : null,
        },
      );
      const movedToOtherAthlete = toAthleteId && toAthleteId !== athleteId;
      const verb = mode === "copy" ? "Copied" : "Moved";
      const where = movedToOtherAthlete
        ? (athletes.find((a) => a.id === toAthleteId)?.name || "another athlete")
        : "another date";
      toast.success(`${verb} to ${where} · ${toDate}`);
      setOpen(false);
      onDone?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Calendar size={18} weight="fill" className="text-moss" />
            Move or copy section
          </DialogTitle>
          <DialogDescription>
            {blockTitle ? `"${blockTitle}" — ` : ""}
            send this whole section (all its movements) to a different date or athlete.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="move-block-form">
          <div>
            <Label className="hs-label">Action</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("move")}
                className={`p-3 rounded-xl border-2 text-left transition-colors ${
                  mode === "move" ? "border-moss bg-sage/40" : "border-line hover:border-line/80"
                }`}
                data-testid="move-mode-move"
              >
                <div className="flex items-center gap-1.5">
                  <ArrowsClockwise size={16} className="text-moss" />
                  <span className="font-semibold text-sm">Move</span>
                </div>
                <p className="text-[11px] text-ink-3 mt-1">Reschedule — original is removed.</p>
              </button>
              <button
                type="button"
                onClick={() => setMode("copy")}
                className={`p-3 rounded-xl border-2 text-left transition-colors ${
                  mode === "copy" ? "border-terracotta bg-terracotta/10" : "border-line hover:border-line/80"
                }`}
                data-testid="move-mode-copy"
              >
                <div className="flex items-center gap-1.5">
                  <Copy size={16} className="text-terracotta" weight="fill" />
                  <span className="font-semibold text-sm">Copy</span>
                </div>
                <p className="text-[11px] text-ink-3 mt-1">Duplicate — keeps the original too.</p>
              </button>
            </div>
          </div>

          <div>
            <Label className="hs-label">Target date</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="mt-2 h-11 rounded-xl font-data"
              data-testid="move-to-date"
              required
            />
          </div>

          <div>
            <Label className="hs-label">Athlete</Label>
            <Select value={toAthleteId} onValueChange={setToAthleteId}>
              <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="move-to-athlete">
                <SelectValue placeholder="Pick an athlete" />
              </SelectTrigger>
              <SelectContent>
                {athletes.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.id === athleteId ? `${a.name} (this athlete)` : a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className={`rounded-xl text-white hover:text-white ${
                mode === "copy" ? "bg-terracotta hover:bg-terracotta-hover" : "bg-moss hover:bg-moss-hover"
              }`}
              data-testid="move-submit"
            >
              {busy ? "Saving…" : mode === "copy" ? "Copy section" : "Move section"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
